// React
import React from "react";

// MUI
import { ListItemIcon, ListItemText, Tooltip } from "@mui/material";

import { useSelector } from "react-redux";
// Third party imports
import { Link } from "react-router-dom";

// Components
import {
  copyLinkItem,
  openInNewTabItem,
  openItem,
  useContextMenu,
} from "components/ui/ContextMenu";

// Styled Components
import { CountPill, rowIconSx, StyledBadge, StyledListItemButton } from "./StyledComponents";

const MAX_COUNT = 99;
const formatCount = (value) => (value > MAX_COUNT ? `${MAX_COUNT}+` : value);

/**
 * One row of the sidebar, used by the main menu.
 * - `to`: in-app path. Without it the row is a button (`onClick`).
 * - `menuPath`: path used by the right-click menu (open, open in a new tab, copy link).
 * - `count`: number shown as a badge on the icon (collapsed) or as a pill (expanded).
 * Collapsed, the label moves to a tooltip.
 */
export const SidebarRow = ({ to, menuPath, label, icon, active = false, open, onClick, count }) => {
  const menu = useContextMenu();
  const path = menuPath || to;
  const getMenuItems = () => [openItem(path), openInNewTabItem(path), copyLinkItem(path)];
  const bound = path ? menu.bind(getMenuItems) : {};

  // A link is the row itself (one tab stop, "open in a new tab" works natively); without `to` it is a button
  const linkProps = to
    ? { component: Link, to, "aria-current": active ? "page" : undefined }
    : { component: "button", type: "button", onClick };

  return (
    <Tooltip title={open ? "" : label} placement="right" disableInteractive>
      <StyledListItemButton
        {...linkProps}
        {...bound}
        active={active}
        aria-label={open ? undefined : label}
        sx={{
          justifyContent: open ? "initial" : "center",
          // collapsed: the icon is centred in the pill
          ...(open ? {} : { px: 0 }),
        }}
      >
        {count && !open ? (
          <StyledBadge overlap="circular" badgeContent={formatCount(count)} color="primary">
            <ListItemIcon sx={rowIconSx(open)}>{icon}</ListItemIcon>
          </StyledBadge>
        ) : (
          <ListItemIcon sx={rowIconSx(open)}>{icon}</ListItemIcon>
        )}
        {open && <ListItemText primary={label} sx={{ m: 0 }} />}
        {open && count ? <CountPill>{formatCount(count)}</CountPill> : null}
      </StyledListItemButton>
    </Tooltip>
  );
};

// Small title above a group of rows. Collapsed, a hairline keeps the groups apart.
export const SectionTitle = ({ open, children }) =>
  open ? (
    <li className="section-title list-none px-6 pt-5 pb-1.5" role="presentation">
      {children}
    </li>
  ) : (
    <li className="mx-5 my-2 list-none border-t border-gray-200" role="presentation" />
  );

const SidebarMenuItem = ({ item, open, isActive, onNotificationClick }) => {
  const unread = useSelector((state) => state.notifications.notificationsUnreadCount);

  return (
    <SidebarRow
      to={item.isNavLink ? item.path : undefined}
      menuPath={item.path}
      label={item.name}
      icon={item.icon}
      active={isActive(item.path) === "true"}
      open={open}
      onClick={item.isNavLink ? undefined : onNotificationClick}
      // the only row that is not a link is the notifications one
      count={item.isNavLink ? 0 : unread}
    />
  );
};

export default SidebarMenuItem;
